/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';

import {
  Row, Col, Card, CardBody,
} from 'reactstrap';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import BaseGrid from '../../components/Common/BaseGrid';
import UnpickedMetric from './unpicked-metric';
import UnpackedMetric from './unpacked-metric';
import UnshippedMetric from './unshipped-metric';
import PickPaceLeaderboard from './pick-pace-leaderboard';
import RecountBanner from './recount-banner';

function Dashboard(props) {
  // eslint-disable-next-line no-unused-vars
  const { t } = props;

  const formatDateValue = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const toStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = toStartOfDay(new Date(Date.now()));

  const [selectedDate, setSelectedDate] = useState(today);
  const [components, setComponents] = useState([]);
  const [offset, setOffset] = useState(0);

  const leaderboardCards = [
    {
      title: t('Top Pickers'),
      subtitle: t('Most productive stop counts for the selected day.'),
      badge: t('Picking'),
      url: `/api/orders/report/top-pickers/${offset}`,
      columns: {
        user: { label: t('Name') },
        stops: { label: t('Stops Picked') },
        picked_qty: { label: t('# of items') },
      },
      pagination: {
        sort: 'stops',
        direction: 'desc',
        perPage: 15,
      },
    },
    {
      title: t('Top Packers'),
      subtitle: t('Best package throughput across the warehouse floor.'),
      badge: t('Packing'),
      url: `/api/orders/report/top-packers/${offset}`,
      columns: {
        user: { label: t('Name') },
        packs: { label: t('Pkgs') },
        items: { label: t('Items') },
      },
      pagination: {
        sort: 'packs',
        direction: 'desc',
        perPage: 15,
      },
    },
    {
      title: t('Top Shippers'),
      subtitle: t('Outbound leaders moving boxes to the dock fastest.'),
      badge: t('Shipping'),
      url: `/api/orders/report/top-shippers/${offset}`,
      columns: {
        user: { label: t('Name') },
        ships: { label: t('Pkgs Shipped') },
      },
      pagination: {
        sort: 'ships',
        direction: 'desc',
        perPage: 15,
      },
    },
  ];

  useEffect(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - offset);
    setSelectedDate(d);
  }, [offset]);

  const handleDateChange = (event) => {
    if (!event.target.value) return;

    const [year, month, day] = event.target.value.split('-').map(Number);
    const pickedDate = new Date(year, month - 1, day);
    const diffMs = today.getTime() - toStartOfDay(pickedDate).getTime();
    const nextOffset = Math.max(0, Math.round(diffMs / 86400000));

    setOffset(nextOffset);
  };

  useEffect(() => {
    // Dynamically import all files from the custom components directory
    const importAll = async (r) => {
      r.keys().forEach(async (key) => {
        if (key.startsWith('./') && key.endsWith('.js')) {
          const component = await import(`../../components/Custom/${key.substr(2)}`);
          setComponents((p) => [...p, component.default]);
        }
      });
    };

    importAll(require.context('../../components/Custom', true, /\.js$/));
  }, []);

  return (
    <div className="page-content dashboard-page">
      <div className="container-fluid">
        <RecountBanner t={t} />
        <Row>
          <Col xl={components.length ? '9' : '12'}>
            <Row>
              <Col xl="12" className="mb-4">
                <div className="dashboard-date-nav">
                  <div className="dashboard-date-nav__label">
                    <span>{t('Viewing')}</span>
                    <strong>{formatDateValue(selectedDate)}</strong>
                  </div>
                  <div className="dashboard-date-nav__actions">
                    <input
                      type="date"
                      className="form-control"
                      value={formatDateValue(selectedDate)}
                      max={formatDateValue(today)}
                      onChange={handleDateChange}
                      aria-label={t('Select date')}
                    />
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setOffset((prev) => prev + 1)}
                      aria-label={t('Increase offset')}
                    >
                      <i className="bx bx-left-arrow-alt" />
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setOffset((prev) => Math.max(prev - 1, 0))}
                      aria-label={t('Decrease offset')}
                    >
                      <i className="bx bx-right-arrow-alt" />
                    </button>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md="4" className="mb-4">
                <Card className="dashboard-metric-card">
                  <CardBody>
                    <UnpickedMetric offset={offset} />
                  </CardBody>
                </Card>
              </Col>
              <Col md="4" className="mb-4">
                <Card className="dashboard-metric-card">
                  <CardBody>
                    <UnpackedMetric offset={offset} />
                  </CardBody>
                </Card>
              </Col>
              <Col md="4" className="mb-4">
                <Card className="dashboard-metric-card">
                  <CardBody>
                    <UnshippedMetric offset={offset} />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              {leaderboardCards.map((board) => (
                <Col lg="4" className="mb-4" key={board.title}>
                  <Card className="dashboard-section-card">
                    <div className="card-header">
                      <div>
                        <h5>{board.title}</h5>
                        <p>{board.subtitle}</p>
                      </div>
                      <span className="dashboard-section-card__badge">{board.badge}</span>
                    </div>
                    <CardBody>
                      <BaseGrid
                        url={board.url}
                        className="dashboard-grid table table-sm align-middle"
                        config={{
                          columns: board.columns,
                          pagination: board.pagination,
                        }}
                      />
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row>
              <Col xl="12" className="mb-4">
                <PickPaceLeaderboard offset={offset} />
              </Col>
            </Row>
          </Col>

          {components.length > 0 && (
            <Col xl="3">
              <div className="dashboard-custom-stack">
                {components.map((Component, index) => (
                  <div key={index}>
                    <Component />
                  </div>
                ))}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}

Dashboard.defaultProps = {
  t: (x) => (x),
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Dashboard));
